import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../config/supabase'
import { validatePassword } from '../utils/validation'
import {
  establishPasswordRecoverySession,
  hasActiveRecoverySession,
  hasPendingPasswordRecovery,
  clearPasswordRecoveryPending,
} from '../utils/passwordRecovery'

const ResetPassword = () => {
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')
  const [isValidToken, setIsValidToken] = useState(false)
  const [checkingToken, setCheckingToken] = useState(true)

  useEffect(() => {
    let cancelled = false

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' && !cancelled) {
        setIsValidToken(true)
        setError('')
        setCheckingToken(false)
      }
    })

    const init = async () => {
      for (let i = 0; i < 8; i += 1) {
        if (await hasActiveRecoverySession()) {
          if (cancelled) return
          setIsValidToken(true)
          setError('')
          setCheckingToken(false)
          return
        }
        await new Promise((resolve) => setTimeout(resolve, 150))
      }

      const pending = hasPendingPasswordRecovery()
      const { data: { session } } = await supabase.auth.getSession()
      if (pending.flag && session?.user) {
        if (cancelled) return
        setIsValidToken(true)
        setError('')
        setCheckingToken(false)
        return
      }

      const result = await establishPasswordRecoverySession()
      if (cancelled) return

      if (result.success) {
        setIsValidToken(true)
        setError('')
      } else {
        setIsValidToken(false)
        setError(
          pending.flag
            ? 'Session expired. Please request a new reset code and try again.'
            : result.error
        )
      }
      setCheckingToken(false)
    }

    init()

    return () => {
      cancelled = true
      subscription.unsubscribe()
    }
  }, [])

  const finishPasswordReset = async () => {
    clearPasswordRecoveryPending()
    await supabase.auth.signOut()
    sessionStorage.setItem('open_login_after_reset', 'true')
    sessionStorage.setItem(
      'auth_success_message',
      'Your password was updated. Please sign in with your new password.'
    )
    navigate('/')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    const newErrors = {}

    const passwordValidation = validatePassword(password)
    if (!passwordValidation.isValid) {
      newErrors.password = passwordValidation.message
    }

    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    setErrors(newErrors)

    if (Object.keys(newErrors).length === 0) {
      setLoading(true)

      try {
        const { error: updateError } = await supabase.auth.updateUser({
          password,
        })

        if (updateError) {
          setError(updateError.message || 'Failed to reset password. Please try again.')
          setLoading(false)
          return
        }

        setSuccess(true)
        setPassword('')
        setConfirmPassword('')
        setLoading(false)

        setTimeout(() => {
          finishPasswordReset()
        }, 3000)
      } catch (err) {
        console.error('Password reset error:', err)
        setError('An unexpected error occurred. Please try again.')
        setLoading(false)
      }
    }
  }

  if (checkingToken) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-primary mb-4"></i>
          <p className="text-gray-600">Verifying reset session...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-12">
      <div className="max-w-md mx-auto px-4 sm:px-6">
        <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
          <div className="mb-6">
            <Link
              to="/"
              className="inline-flex items-center text-primary hover:text-purple-700 transition mb-4"
            >
              <i className="fas fa-arrow-left mr-2"></i>
              Back to Home
            </Link>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-key text-primary text-2xl"></i>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                {success ? 'Password Reset Successful!' : 'Reset Your Password'}
              </h1>
              {!success && isValidToken && (
                <p className="text-sm text-gray-600">Enter your new password below</p>
              )}
            </div>
          </div>

          {error && isValidToken && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <i className="fas fa-exclamation-circle text-red-600 mr-2"></i>
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            </div>
          )}

          {success ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-check text-green-600 text-2xl"></i>
              </div>
              <p className="text-gray-600 mb-6">
                Your password has been successfully reset. You will be signed out and redirected to sign in shortly.
              </p>
              <button
                onClick={finishPasswordReset}
                className="w-full bg-secondary text-white py-3 rounded-lg hover:bg-gray-800 transition text-base font-semibold"
              >
                Go to Sign In
              </button>
            </div>
          ) : isValidToken ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value)
                    if (errors.password) setErrors({ ...errors, password: '' })
                  }}
                  required
                  placeholder="Enter your new password"
                  disabled={loading}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed ${
                    errors.password ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.password && (
                  <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value)
                    if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: '' })
                  }}
                  required
                  placeholder="Confirm your new password"
                  disabled={loading}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed ${
                    errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.confirmPassword && (
                  <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-secondary text-white py-3 rounded-lg hover:bg-gray-800 transition text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    <span>Resetting Password...</span>
                  </>
                ) : (
                  'Reset Password'
                )}
              </button>
            </form>
          ) : (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-exclamation-triangle text-red-600 text-2xl"></i>
              </div>
              <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                {error || 'Invalid or expired reset session. Please request a new reset code.'}
              </p>
              <button
                onClick={() => navigate('/verify-reset-code')}
                className="w-full bg-secondary text-white py-3 rounded-lg hover:bg-gray-800 transition text-base font-semibold mb-3"
              >
                Enter reset code
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg hover:bg-gray-200 transition text-base font-semibold"
              >
                Back to Home
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ResetPassword
